import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Labels, Print, type LabelRecord, type QueueItem, type WineLabelPanePayload, type WineLabelReview } from '@/lib/api';
import {
  Button, Card, EmptyState, Icon, Input, Skeleton, Tabs, Badge, IconButton, Modal, Textarea,
} from '@/design-system';
import { useToasts, usePrefs } from '@/lib/store';
import { formatDate } from '@/lib/format';

export function LabelsPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'queue' | 'all'>('queue');
  const [showNew, setShowNew] = useState(false);

  return (
    <div className="grid gap-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="ui-section-title text-3xl">{t('labels.title')}</h1>
          <p className="text-sm text-mist">{t('labels.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Tabs<'queue' | 'all'>
            tabs={[
              { id: 'queue', label: t('labels.queue'), icon: 'clock' },
              { id: 'all', label: 'Alle', icon: 'list' },
            ]}
            active={tab}
            onChange={setTab}
          />
          <Button variant="primary" iconLeft="plus" onClick={() => setShowNew(true)}>
            {t('labels.new')}
          </Button>
        </div>
      </header>

      <PrinterStatus />

      {tab === 'queue' ? <QueueView /> : <AllLabelsView />}

      <Modal open={showNew} onClose={() => setShowNew(false)} size="lg" title={t('labels.new')}>
        <NewLabelForm onClose={() => setShowNew(false)} />
      </Modal>
    </div>
  );
}

function PrinterStatus() {
  const statusQ = useQuery({ queryKey: ['print.status'], queryFn: () => Print.status() });
  const printersQ = useQuery({ queryKey: ['print.printers'], queryFn: () => Print.printers() });
  const { printTarget, setPrintTarget } = usePrefs();
  const { push } = useToasts();
  const [manualPrinterName, setManualPrinterName] = useState(printTarget?.printerName ?? '');

  if (statusQ.isLoading) {
    return <Skeleton height={48} rounded="md" />;
  }

  return (
    <Card className="flex flex-wrap items-center gap-3 p-3">
      <Icon name="print" className="text-candle" />
      <div className="grid">
        <span className="text-xs uppercase tracking-widest text-mist">Print-modus</span>
        <span className="text-sm text-parchment">
          {statusQ.data?.localPrinter.mode ?? '—'} · {printTarget?.printerName ?? 'kun kø'}
        </span>
      </div>
      {(printersQ.data?.printers.length ?? 0) > 0 && (
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-mist">Skriver:</span>
          <select
            className="ui-input w-auto text-sm"
            value={printTarget?.printerName ?? ''}
            onChange={(e) =>
              setPrintTarget({
                type: 'local',
                printerName: e.target.value || undefined,
              })
            }
          >
            <option value="">— velg —</option>
            {printersQ.data!.printers.map((p) => (
              <option key={p.name} value={p.name}>
                {p.name}{p.isDefault ? ' (default)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="ml-auto flex min-w-[260px] flex-wrap items-end gap-2">
        <Input
          label="Manuell skriver"
          className="min-w-[220px]"
          placeholder="Zebra_Technologies_ZTC_ZD421_203dpi_ZPL"
          value={manualPrinterName}
          onChange={(e) => setManualPrinterName(e.target.value)}
        />
        <Button
          size="sm"
          variant="ghost"
          iconLeft="print"
          onClick={() => {
            const printerName = manualPrinterName.trim();
            setPrintTarget(printerName ? { type: 'local', printerName } : { type: 'queue' });
            push({
              title: printerName ? 'Skriver lagret' : 'Print satt til kø',
              description: printerName || undefined,
              tone: printerName ? 'success' : 'info',
            });
          }}
        >
          Lagre
        </Button>
      </div>
    </Card>
  );
}

function QueueView() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { push } = useToasts();
  const { printTarget } = usePrefs();
  const queueQ = useQuery({
    queryKey: ['labels.queue'],
    queryFn: () => Labels.queue(),
    refetchInterval: 4000,
  });
  const pause = useMutation({
    mutationFn: Labels.pause,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['labels.queue'] }),
    onError: (err: Error) => push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });
  const resume = useMutation({
    mutationFn: Labels.resume,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['labels.queue'] });
      void qc.invalidateQueries({ queryKey: ['labels.list'] });
    },
    onError: (err: Error) => push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });
  const reprint = useMutation({
    mutationFn: (labelId: string) => Labels.print(labelId, labelPrintPayload(printTarget)),
    onSuccess: () => {
      push({ title: 'Sendt til print', tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['labels.queue'] });
      void qc.invalidateQueries({ queryKey: ['labels.list'] });
    },
    onError: (err: Error) => push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });
  const clear = useMutation({
    mutationFn: Labels.clearCompleted,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['labels.queue'] }),
    onError: (err: Error) => push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });

  if (queueQ.isLoading) {
    return <div className="grid gap-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={56} rounded="md" />)}</div>;
  }
  const items = queueQ.data?.queue ?? [];
  if (items.length === 0) {
    return <EmptyState icon="clock" title="Køen er tom" description="Ingen jobber i venter akkurat nå." />;
  }
  return (
    <div className="grid gap-3">
      <div className="flex justify-end">
        <Button variant="ghost" size="sm" iconLeft="trash" onClick={() => clear.mutate()} loading={clear.isPending}>
          {t('labels.clearCompleted')}
        </Button>
      </div>
      <ul className="grid gap-2">
        {items.map((q) => (
          <li key={q.id} className="ui-card grid grid-cols-[auto_1fr_auto] items-center gap-3 p-3">
            <Icon name="tag" className="text-candle" size={16} />
            <div className="grid">
              <span className="text-sm text-parchment">{q.title || q.labelId}</span>
              <span className="text-xs text-mist">
                {formatDate(q.createdAt)} · {q.copies}×{q.printerName ? ` · ${q.printerName}` : ''}
              </span>
              {q.lastError && <span className="text-xs text-ruby">{q.lastError}</span>}
            </div>
            <div className="flex items-center gap-2">
              <Badge tone={queueStatusTone(q.status)}>
                {t(`labels.statuses.${q.status}`)}
              </Badge>
              {(q.status === 'completed' || q.status === 'failed') && (
                <Button
                  size="sm"
                  variant="ghost"
                  iconLeft="print"
                  onClick={() => reprint.mutate(q.labelId)}
                  loading={reprint.isPending && reprint.variables === q.labelId}
                >
                  {q.status === 'failed' ? t('labels.retryPrint') : t('labels.reprint')}
                </Button>
              )}
              {q.status === 'pending' && <IconButton icon="moon" label={t('labels.pause')} size="sm" onClick={() => pause.mutate(q.id)} disabled={pause.isPending} />}
              {q.status === 'paused' && <IconButton icon="sun" label={t('labels.resume')} size="sm" onClick={() => resume.mutate(q.id)} />}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AllLabelsView() {
  const { t } = useTranslation();
  const labelsQ = useQuery({ queryKey: ['labels.list'], queryFn: () => Labels.list() });
  const qc = useQueryClient();
  const { push } = useToasts();
  const remove = useMutation({
    mutationFn: Labels.destroy,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['labels.list'] });
      void qc.invalidateQueries({ queryKey: ['labels.queue'] });
    },
    onError: (err: Error) => push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });
  const { printTarget } = usePrefs();
  const printNow = useMutation({
    mutationFn: (id: string) => Labels.print(id, labelPrintPayload(printTarget)),
    onSuccess: () => {
      push({ title: 'Sendt til print', tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['labels.queue'] });
      void qc.invalidateQueries({ queryKey: ['labels.list'] });
    },
    onError: (err: Error) => push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });

  if (labelsQ.isLoading) {
    return <div className="grid gap-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={64} rounded="md" />)}</div>;
  }
  const items = labelsQ.data?.labels ?? [];
  if (items.length === 0) {
    return <EmptyState icon="tag" title="Ingen etiketter enda" description="Lag din første etikett." />;
  }
  return (
    <ul className="grid gap-3">
      {items.map((l) => (
        <LabelRow key={l.id} label={l} onPrint={() => printNow.mutate(l.id)} onDelete={() => remove.mutate(l.id)} />
      ))}
    </ul>
  );
}

function LabelRow({ label, onPrint, onDelete }: { label: LabelRecord; onPrint: () => void; onDelete: () => void }) {
  const { t } = useTranslation();
  const main = firstPane(label);
  const title = main.productName || main.line1 || label.productName || label.title || '—';
  const details = [main.line2, main.line3, main.line4, main.line5, main.line6].filter(Boolean).join(' · ');
  return (
    <li className="ui-card grid gap-3 p-4 sm:grid-cols-[1fr_auto] sm:items-center">
      <div className="grid gap-1">
        <div className="flex items-center gap-2">
          <Icon name="tag" className="text-candle" size={14} />
          <span className="font-serif text-base text-parchment">{title}</span>
          <Badge tone="mist">{label.quantity}×</Badge>
          <Badge tone={labelStatusTone(label.status)}>{t(`labels.statuses.${label.status}`)}</Badge>
        </div>
        {details && <div className="text-xs text-mist">{details}</div>}
        {label.lastError && <div className="text-xs text-ruby">{label.lastError}</div>}
        <div className="text-[10px] uppercase tracking-widest text-mist">{label.id} · {formatDate(label.createdAt)}</div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="primary" iconLeft="print" onClick={onPrint}>{t('labels.print')}</Button>
        <Button size="sm" variant="ghost" iconLeft="trash" onClick={onDelete}>{t('labels.delete')}</Button>
      </div>
    </li>
  );
}

function firstPane(label: LabelRecord) {
  const legacy = label as LabelRecord & { payload?: WineLabelPanePayload & { labels?: WineLabelPanePayload[] } };
  return label.panes?.[0] ?? legacy.payload?.labels?.[0] ?? legacy.payload ?? {};
}

function queueStatusTone(status: QueueItem['status']): 'gold' | 'ruby' | 'mist' | 'success' | 'danger' {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'danger';
  if (status === 'paused') return 'mist';
  return 'gold';
}

function labelStatusTone(status: LabelRecord['status']): 'gold' | 'ruby' | 'mist' | 'success' | 'danger' {
  if (status === 'printed') return 'success';
  if (status === 'cancelled') return 'mist';
  return 'gold';
}

function labelPrintPayload(printTarget: { printerName?: string; type?: string } | null) {
  return printTarget
    ? { printerName: printTarget.printerName, deliveryTarget: printTarget }
    : {};
}

function NewLabelForm({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { push } = useToasts();
  const { printTarget } = usePrefs();
  const [pane, setPane] = useState<WineLabelPanePayload>({});
  const [quantity, setQuantity] = useState(1);
  const [debugFrame, setDebugFrame] = useState(false);
  const [reviewAnswer, setReviewAnswer] = useState<'accepted' | 'issue' | null>(null);

  const previewQ = useMutation({
    mutationFn: () => Labels.preview({ ...pane, quantity, debugFrame }),
    onSuccess: () => setReviewAnswer(null),
  });
  const create = useMutation({
    mutationFn: () => Labels.create({
      ...pane,
      quantity,
      debugFrame,
      printerName: printTarget?.printerName,
      deliveryTarget: printTarget ?? undefined,
    }),
    onSuccess: () => {
      push({ title: 'Etikett opprettet', tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['labels.list'] });
      void qc.invalidateQueries({ queryKey: ['labels.queue'] });
      onClose();
    },
    onError: (err: Error) => push({ title: t('common.error'), description: err.message, tone: 'danger' }),
  });

  return (
    <div className="grid gap-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <Input label={t('labels.fields.productName')} value={pane.productName ?? ''} onChange={(e) => setPane((p) => ({ ...p, productName: e.target.value }))} />
        <Input label={t('labels.fields.cuvee')} value={pane.cuvee ?? ''} onChange={(e) => setPane((p) => ({ ...p, cuvee: e.target.value }))} />
        <Input label={t('labels.fields.producer')} value={pane.producer ?? ''} onChange={(e) => setPane((p) => ({ ...p, producer: e.target.value }))} />
        <Input label={t('labels.fields.vintage')} value={pane.vintage ?? ''} onChange={(e) => setPane((p) => ({ ...p, vintage: e.target.value }))} />
        <Input label={t('labels.fields.region')} value={pane.region ?? ''} onChange={(e) => setPane((p) => ({ ...p, region: e.target.value }))} />
        <Input label={t('labels.fields.country')} value={pane.country ?? ''} onChange={(e) => setPane((p) => ({ ...p, country: e.target.value }))} />
        <Input label={t('labels.fields.wineClass')} value={pane.wineClass ?? ''} onChange={(e) => setPane((p) => ({ ...p, wineClass: e.target.value }))} />
        <Input label={t('labels.fields.typeCode')} value={pane.typeCode ?? ''} onChange={(e) => setPane((p) => ({ ...p, typeCode: e.target.value }))} />
        <Input label={t('labels.fields.valueRange')} value={pane.valueRange ?? ''} onChange={(e) => setPane((p) => ({ ...p, valueRange: e.target.value }))} />
        <Input label={t('labels.fields.barcode')} value={pane.barcode ?? ''} onChange={(e) => setPane((p) => ({ ...p, barcode: e.target.value }))} />
      </div>
      <Textarea
        label={t('labels.fields.foodPairings') + ` (komma-separert)`}
        rows={2}
        value={pane.foodPairings?.join(', ') ?? ''}
        onChange={(e) => setPane((p) => ({ ...p, foodPairings: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) }))}
      />
      <Input
        label={t('labels.fields.occasion')}
        value={pane.occasion ?? ''}
        onChange={(e) => setPane((p) => ({ ...p, occasion: e.target.value }))}
      />
      <div className="grid grid-cols-3 gap-3">
        <Input
          label="Antall"
          type="number"
          min={1}
          max={50}
          value={quantity}
          onChange={(e) => setQuantity(Number(e.target.value))}
        />
        <label className="flex items-center gap-2 text-sm text-mist">
          <input type="checkbox" checked={debugFrame} onChange={(e) => setDebugFrame(e.target.checked)} />
          Debug-ramme
        </label>
      </div>

      {/* preview */}
      {previewQ.data && (
        <div className="grid gap-3">
          <LabelReviewHelper
            review={previewQ.data.rendered.review}
            answer={reviewAnswer}
            onAccept={() => {
              setReviewAnswer('accepted');
              push({ title: 'Etikett godkjent', tone: 'success' });
            }}
            onIssue={() => {
              setReviewAnswer('issue');
              push({ title: 'Juster etiketten før print', tone: 'info' });
            }}
          />
          <div className="ui-card p-3">
            <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-widest text-mist">
              <Icon name="eye" size={12} /> Preview
            </div>
            <pre className="max-h-48 overflow-auto whitespace-pre text-[11px] text-parchment-dim">{previewQ.data.rendered.zpl}</pre>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <Button variant="ghost" iconLeft="eye" onClick={() => previewQ.mutate()} loading={previewQ.isPending}>{t('labels.preview')}</Button>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" iconLeft="check" onClick={() => create.mutate()} loading={create.isPending}>{t('common.save')}</Button>
        </div>
      </div>
    </div>
  );
}

function LabelReviewHelper({
  review,
  answer,
  onAccept,
  onIssue,
}: {
  review?: WineLabelReview;
  answer: 'accepted' | 'issue' | null;
  onAccept: () => void;
  onIssue: () => void;
}) {
  if (!review) return null;
  const warningCount = review.warnings.length;
  return (
    <div className="ui-card grid gap-3 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Icon name="sparkles" size={14} className="text-candle" />
        <span className="text-xs uppercase tracking-widest text-mist">Etiketthjelper</span>
        <Badge tone={warningCount ? 'gold' : 'success'}>{warningCount ? `${warningCount} sjekk` : 'OK'}</Badge>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {review.panes.map((pane) => (
          <div key={pane.paneIndex} className="rounded-md border border-line-soft p-2">
            <div className="mb-1 flex items-center gap-2 text-[10px] uppercase tracking-widest text-mist">
              <span>{pane.paneIndex === 0 ? 'Venstre' : 'Høyre'}</span>
              {pane.hasQr && <Badge tone="mist">QR</Badge>}
            </div>
            <div className="grid gap-0.5 font-mono text-[11px] text-parchment-dim">
              {pane.lines.filter(Boolean).map((line, index) => (
                <span key={`${pane.paneIndex}-${index}`} className="truncate">{line}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
      {warningCount > 0 && (
        <ul className="grid gap-1 text-xs text-mist">
          {review.warnings.slice(0, 3).map((warning) => (
            <li key={`${warning.paneIndex}-${warning.lineIndex}-${warning.fitted}`}>
              {warning.message}: {warning.fitted}
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm text-parchment">{review.question}</span>
        <div className="flex gap-2">
          <Button size="sm" variant={answer === 'issue' ? 'ruby' : 'ghost'} onClick={onIssue}>
            {review.issueLabel}
          </Button>
          <Button size="sm" variant={answer === 'accepted' ? 'primary' : 'ghost'} iconLeft="check" onClick={onAccept}>
            {review.okLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
